(() => {
  'use strict';
  const rootRoute = () => window.Shopify?.routes?.root || '/';
  const route = (path) => rootRoute() + path;
  const queryAll = (root, selector) => [...root.querySelectorAll(selector)];

  function initializeQuantity(root) {
    queryAll(root, '[data-quantity-control]').forEach((control) => {
      if (control.dataset.initialized) return;
      const input = control.querySelector('input');
      if (!input) return;
      control.dataset.initialized = 'true';
      control.addEventListener('click', (event) => {
        const button = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
        if (!button) return;
        const minimum = Number(input.min || 0);
        const maximum = input.max ? Number(input.max) : Infinity;
        const step = Number(input.step || 1);
        const value = Number(input.value) || minimum;
        input.value = Math.max(minimum, Math.min(maximum, value + (button.hasAttribute('data-quantity-plus') ? step : -step)));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  function initializeGallery(root) {
    queryAll(root, '[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.initialized) return;
      gallery.dataset.initialized = 'true';
      const slides = queryAll(gallery, '[data-media-id]');
      const thumbnails = queryAll(gallery, '[data-gallery-target]');
      let currentIndex = Math.max(0, slides.findIndex((slide) => !slide.hidden));
      const alignThumbnails = () => {
        const active = thumbnails[currentIndex];
        const strip = gallery.querySelector('[data-gallery-thumbnails]');
        if (!active || !strip) return;
        const relativeLeft = active.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft;
        strip.scrollTo({ left: relativeLeft, behavior: 'instant' });
      };
      const show = (index) => {
        if (!slides.length) return;
        currentIndex = Math.max(0, Math.min(index, slides.length - 1));
        const previous = gallery.querySelector('[data-gallery-previous]');
        const next = gallery.querySelector('[data-gallery-next]');
        if (previous) previous.disabled = currentIndex === 0;
        if (next) next.disabled = currentIndex === slides.length - 1;
        slides.forEach((slide, i) => {
          slide.hidden = i !== currentIndex;
          if (slide.hidden) {
            slide.querySelectorAll('video').forEach((video) => video.pause());
            slide.querySelectorAll('iframe').forEach((frame) => {
              if (frame.src.includes('youtube')) frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), 'https://www.youtube.com');
              if (frame.src.includes('vimeo')) frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), 'https://player.vimeo.com');
            });
          }
        });
        thumbnails.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === currentIndex)));
        alignThumbnails();
      };
      gallery.addEventListener('click', (event) => {
        const thumbnail = event.target.closest('[data-gallery-target]');
        if (thumbnail) show(slides.findIndex((slide) => slide.dataset.mediaId === thumbnail.dataset.galleryTarget));
        if (event.target.closest('[data-gallery-previous]')) show(currentIndex - 1);
        if (event.target.closest('[data-gallery-next]')) show(currentIndex + 1);
      });
      gallery.addEventListener('keydown', (event) => {
        if (!event.target.closest('button') || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        show(currentIndex + (event.key === 'ArrowRight' ? 1 : -1));
      });
      gallery.addEventListener('product:media', (event) => {
        const index = slides.findIndex((slide) => slide.dataset.mediaId === String(event.detail.id));
        if (index >= 0) show(index);
      });
      show(currentIndex);
      const strip = gallery.querySelector('[data-gallery-thumbnails]');
      if (strip && 'ResizeObserver' in window) {
        const observer = new ResizeObserver(() => {
          if (gallery.isConnected) alignThumbnails();
          else observer.disconnect();
        });
        observer.observe(strip);
      }
    });
  }

  function initializeProductPanels(root) {
    queryAll(root, '[data-product-panel]').forEach((panel) => {
      if (panel.dataset.initialized) return;
      const form = panel.querySelector('[data-product-form]');
      const data = panel.querySelector('[data-product-variants]');
      if (!form || !data) return;
      let variants;
      try { variants = JSON.parse(data.textContent); } catch { return; }
      panel.dataset.initialized = 'true';
      const variantInput = form.querySelector('[data-variant-id]');
      const quantityInput = form.querySelector('[name="quantity"]');
      const addButton = form.querySelector('[data-add-to-cart]');
      const buyButton = form.querySelector('[data-buy-now]');
      const status = panel.querySelector('[data-product-status]');
      const scope = panel.closest('[data-product-scope]') || panel.parentElement;
      let variant = variants.find((entry) => String(entry.id) === variantInput.value) || variants[0];
      let busy = false;
      variantInput.disabled = !variant;
      const setStatus = (message, error = false, cartLink = false) => {
        status.replaceChildren(document.createTextNode(message));
        status.hidden = !message;
        status.dataset.error = String(error);
        if (cartLink) {
          const link = document.createElement('a');
          link.href = route('cart');
          link.textContent = 'View cart';
          status.append(link);
        }
      };
      const updateVariant = () => {
        const groups = queryAll(form, '[data-option-position]');
        const options = groups.map((group) => group.querySelector('input:checked')?.value);
        variant = variants.find((entry) => entry.options.every((option, i) => option === options[i])) || (groups.length ? undefined : variants[0]);
        variantInput.value = variant ? variant.id : '';
        variantInput.disabled = !variant;
        const available = Boolean(variant?.available);
        addButton.disabled = busy || !available;
        buyButton.disabled = busy || !available;
        addButton.textContent = variant ? (available ? 'ADD TO CART' : 'SOLD OUT') : 'UNAVAILABLE';
        setStatus('');
        if (!variant) return;
        panel.dataset.variantSku = variant.sku || '';
        scope.querySelectorAll('[data-product-sku]').forEach((item) => { item.textContent = variant.sku || ''; });
        panel.querySelector('[data-product-price]').textContent = variant.priceFormatted;
        const compare = panel.querySelector('[data-compare-price]');
        compare.textContent = variant.compareAtPriceFormatted;
        compare.hidden = !(variant.compareAtPrice > variant.price);
        quantityInput.min = variant.quantityMin || 1;
        quantityInput.step = variant.quantityIncrement || 1;
        if (variant.quantityMax != null) quantityInput.max = variant.quantityMax;
        else quantityInput.removeAttribute('max');
        const minimum = Number(quantityInput.min);
        const maximum = variant.quantityMax ?? Infinity;
        const increment = Number(quantityInput.step);
        const currentQuantity = Number(quantityInput.value) || minimum;
        quantityInput.value = Math.min(maximum, minimum + Math.max(0, Math.ceil((currentQuantity - minimum) / increment)) * increment);
        const gallery = scope.querySelector('[data-product-gallery]');
        if (gallery && variant.featuredMediaId) gallery.dispatchEvent(new CustomEvent('product:media', { detail: { id: variant.featuredMediaId } }));
        const homeImage = scope.querySelector('[data-featured-product-image]');
        if (homeImage && variant.featuredImage) {
          homeImage.src = variant.featuredImage;
          homeImage.removeAttribute('srcset');
        }
        if (panel.dataset.context === 'product') {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }
        panel.dispatchEvent(new CustomEvent('product:variant-change', { bubbles: true, detail: { variant } }));
      };
      panel.querySelector('[data-variant-options]')?.addEventListener('change', updateVariant);
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (busy || !variant?.available || !form.reportValidity()) return;
        busy = true;
        const checkout = event.submitter?.hasAttribute('data-buy-now');
        addButton.disabled = true;
        buyButton.disabled = true;
        form.setAttribute('aria-busy', 'true');
        setStatus('Adding to cart…');
        try {
          const body = new FormData(form);
          body.delete('return_to');
          const response = await fetch(route('cart/add.js'), { method: 'POST', body, headers: { Accept: 'application/json' }, credentials: 'same-origin' });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this product. Please try again.');
          if (checkout) {
            window.location.assign(route('checkout'));
            return;
          }
          setStatus('Added to cart.', false, true);
          // A count refresh failure must never retry an already successful add.
          try {
            const cartResponse = await fetch(route('cart.js'), { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
            if (cartResponse.ok) {
              const cart = await cartResponse.json();
              document.querySelectorAll('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; count.hidden = cart.item_count === 0; });
              document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
            }
          } catch { /* The cart page remains the source of truth. */ }
        } catch (error) {
          setStatus(error.message || 'Unable to add this product. Please try again.', true);
        } finally {
          busy = false;
          form.removeAttribute('aria-busy');
          addButton.disabled = !variant?.available;
          buyButton.disabled = !variant?.available;
        }
      });
    });
  }

  function initializeTabs(root) {
    queryAll(root, '[data-product-tabs]').forEach((tabs) => {
      if (tabs.dataset.initialized) return;
      tabs.dataset.initialized = 'true';
      const buttons = queryAll(tabs, '[data-tab-target]');
      const panels = queryAll(tabs, '[data-tab-panel]');
      let selected = 'detail';
      const activate = (name) => {
        selected = name;
        buttons.forEach((button) => { const active = button.dataset.tabTarget === name; button.setAttribute('aria-selected', String(active)); button.tabIndex = active ? 0 : -1; });
        panels.forEach((panel) => { panel.open = panel.dataset.tabPanel === name; });
      };
      buttons.forEach((button, index) => {
        button.addEventListener('click', () => activate(button.dataset.tabTarget));
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = (index + (event.key === 'ArrowLeft' ? -1 : 1) + buttons.length) % buttons.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = buttons.length - 1;
          activate(buttons[next].dataset.tabTarget);
          buttons[next].focus();
        });
      });
      const desktop = window.matchMedia('(min-width: 768px)');
      desktop.addEventListener('change', () => { if (desktop.matches) activate(selected); });
    });
  }

  function initialize(root = document) {
    initializeQuantity(root);
    initializeGallery(root);
    initializeProductPanels(root);
    initializeTabs(root);
    queryAll(root, '[data-native-reviews]').forEach((section) => {
      if (section.dataset.initialized) return;
      section.dataset.initialized = 'true';
      const cards = queryAll(section, '[data-review-card]');
      const grid = section.querySelector('.native-reviews__grid');
      const rating = section.querySelector('[data-review-filter]');
      const images = section.querySelector('[data-review-images]');
      const sort = section.querySelector('[data-review-sort]');
      const empty = section.querySelector('[data-review-empty]');
      const refresh = () => {
        let visible = 0;
        cards.forEach((card) => {
          card.hidden = Boolean((rating && rating.value !== 'all' && card.dataset.rating !== rating.value) || (images?.value === 'images' && card.dataset.hasImage !== 'true'));
          if (!card.hidden) visible += 1;
        });
        if (empty) empty.hidden = visible > 0;
        if (grid) [...cards].sort((a, b) => sort?.value === 'highest' ? Number(b.dataset.rating) - Number(a.dataset.rating) : sort?.value === 'lowest' ? Number(a.dataset.rating) - Number(b.dataset.rating) : (Date.parse(b.dataset.reviewDate) || 0) - (Date.parse(a.dataset.reviewDate) || 0)).forEach((card) => grid.append(card));
      };
      [rating, images, sort].forEach((select) => select?.addEventListener('change', refresh));
      queryAll(section, '[data-review-rating]').forEach((button) => button.addEventListener('click', () => { if (rating) { rating.value = button.dataset.reviewRating; refresh(); } }));
    });
    queryAll(root, '[data-collection-sort]').forEach((form) => {
      if (form.dataset.initialized) return;
      form.dataset.initialized = 'true';
      form.querySelector('select')?.addEventListener('change', () => form.requestSubmit());
    });
    queryAll(root, '[data-collection-filter]').forEach((form) => {
      if (form.dataset.initialized) return;
      form.dataset.initialized = 'true';
      form.addEventListener('change', () => form.requestSubmit());
    });
    queryAll(root, '[data-share-product]').forEach((button) => {
      if (button.dataset.initialized) return;
      button.dataset.initialized = 'true';
      button.addEventListener('click', async () => {
        const status = button.parentElement.querySelector('[data-share-status]');
        try {
          if (navigator.share) await navigator.share({ title: button.dataset.shareTitle, url: window.location.href });
          else { await navigator.clipboard.writeText(window.location.href); status.textContent = 'Link copied'; }
        } catch (error) {
          if (error.name !== 'AbortError') status.textContent = 'Copy the page address to share this product.';
        }
      });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
